
import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { ArrowLeft, Map, Compass } from "lucide-react";
import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <Container className="min-h-[calc(100vh-200px)] flex items-center justify-center py-20">
      <SEO 
        title="Page Not Found" 
        description="The page you're looking for seems to have wandered off the map."
        noIndex={true}
      />
      <div className="w-full max-w-3xl mx-auto">
        <div className="relative flex flex-col items-center justify-center text-center space-y-6">
          {/* Animated 404 */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="text-[150px] font-bold text-primary/20 leading-none select-none">
              404
            </div>
            <motion.div 
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Map className="w-24 h-24 text-primary/70 rotate-12" />
            </motion.div>
          </motion.div>

          {/* Main content */}
          <motion.div
            className="space-y-6 max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Destination not found
            </h1>
            
            <p className="text-lg text-gray-600">
              The page you're looking for seems to have wandered off the map.
              Let's get you back on track!
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-8">
              <Button 
                variant="default" 
                size="lg"
                className="bg-primary hover:bg-primary/90 w-full md:w-auto"
                asChild
              >
                <Link to="/">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Link>
              </Button>
              
              <Button 
                variant="outline" 
                size="lg"
                className="w-full md:w-auto"
                asChild
              >
                <Link to="/search">
                  <Compass className="mr-2 h-4 w-4" />
                  Explore Destinations
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Decorative elements */}
          <motion.div 
            className="absolute -z-10 inset-0 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ delay: 0.5, duration: 1 }}
          >
            <div className="absolute top-10 left-10 w-20 h-20 rounded-full bg-purple-200/40 blur-xl"></div>
            <div className="absolute bottom-10 right-20 w-32 h-32 rounded-full bg-blue-200/30 blur-xl"></div>
            <div className="absolute top-40 right-10 w-16 h-16 rounded-full bg-orange-200/30 blur-lg"></div>
          </motion.div>
        </div>
      </div>
    </Container>
  );
};

export default NotFound;
